'use client';

import { isArray } from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { stringify } from 'qs';
import { useCallback, useMemo, useState, useTransition } from 'react';
import { useUpdateEffect } from 'react-use';
import { z } from 'zod';

type SearchHandler<T> = {
  set: ((params: Partial<T>) => void) & ((updater: (old: T) => Partial<T>) => void);
  clear: (keys?: keyof T | Array<keyof T>) => void;
};

type UseSearchOptions<T = Record<string, any>> = {
  /** 可选的 Zod schema，用于验证和转换搜索参数 */
  schema?: z.ZodSchema<T>;
  /** 是否在参数变化时滚动到页面顶部，默认为 false */
  scroll?: boolean;
};

/**
 * 生成查询字符串
 *
 * 该函数将表单数据过滤掉值为空或为空数组的字段，并使用 `qs.stringify` 生成
 * 一个格式化的查询字符串。它支持数组格式的参数，并跳过值为 `null` 的字段。
 *
 * @param data - 需要转换为查询字符串的对象。对象的键值对将会被处理并转换成查询字符串。
 *               数据的每个值可以是一个字符串、数组或其他类型。
 * @returns 生成的查询字符串。比如：`?key=value&arr=val1&arr=val2`
 */

function generateQueryString(data: Record<string, any>): string {
  // 过滤数据，删除值为空或为空数组的字段
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([, value]) => {
      if (isArray(value)) {
        return value.length > 0; // 只保留非空数组
      }
      if (typeof value === 'string') {
        return value.trim() !== ''; // 对字符串类型，去除空字符串
      }
      if (typeof value === 'object' && value !== null) {
        // 处理嵌套对象参数
        const nestedValues = Object.values(value as Record<string, unknown>);
        return nestedValues.some((v) => (typeof v === 'string' ? v.trim() !== '' : Boolean(v)));
      }
      return value !== undefined;
    })
  );
  // 使用 qs.stringify 生成查询字符串，跳过 null 值，并采用 "repeat" 数组格式
  return stringify(filteredData, {
    skipNulls: true, // 跳过值为 null 的字段
    arrayFormat: 'repeat', // 对数组采用重复格式，例如 key=val1&key=val2
    allowDots: true, // 允许使用点号表示嵌套对象
  });
}

/**
 * useSearch Hook
 *
 * 用于处理 URL 搜索参数的 React Hook，支持 Zod schema 验证和类型转换
 *
 * @param options - 配置选项，包含 schema 和其他设置
 * @returns 包含搜索参数、处理函数和加载状态的对象
 */
export function useSearch<T extends Record<string, any>>(
  options: UseSearchOptions<T> = {}
): {
  search: T;
  searchHandler: SearchHandler<T>;
  pending: boolean;
} {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const { schema, scroll = false } = options;
  const searchParams = useSearchParams();

  // 使用 state 保存 search，初始值从 URL 解析
  const [search, setSearch] = useState<T>(() => {
    // 将 URLSearchParams 转换为普通对象
    const rawParams: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      // 处理多值参数（同一个 key 有多个值的情况）
      if (rawParams[key]) {
        if (Array.isArray(rawParams[key])) {
          rawParams[key].push(value);
        } else {
          rawParams[key] = [rawParams[key], value];
        }
      } else {
        rawParams[key] = value;
      }
    });
    // 使用 Zod schema 进行验证和转换
    const result = schema ? schema.parse(rawParams) : rawParams;
    return result as T;
  });

  // 监听 searchParams 的变化并同步到 state
  useUpdateEffect(() => {
    const rawParams: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (rawParams[key]) {
        if (Array.isArray(rawParams[key])) {
          rawParams[key].push(value);
        } else {
          rawParams[key] = [rawParams[key], value];
        }
      } else {
        rawParams[key] = value;
      }
    });
    const result = schema ? schema.parse(rawParams) : rawParams;
    setSearch(result as T);
  }, [searchParams, schema]);

  const handleSearch = useCallback(
    (newSearch: T) => {
      // 1. 先立即更新 state（同步更新，UI 立即响应）
      setSearch(newSearch as T);
      // 2. 然后更新路由（异步）
      const queryString = generateQueryString(newSearch);
      startTransition(() => router.push(`?${queryString}`, { scroll }));
    },
    [router, scroll]
  );

  const searchHandler = useMemo<SearchHandler<T>>(
    () => ({
      set: (paramsOrUpdater: Partial<T> | ((old: T) => Partial<T>)) => {
        let newParams: Partial<T>;

        if (typeof paramsOrUpdater === 'function') {
          // updater 函数模式：(old) => new
          const updater = paramsOrUpdater;
          newParams = updater(search);
        } else {
          // 直接传入新值模式
          newParams = paramsOrUpdater;
        }

        // 合并新参数
        const mergedParams = { ...search, ...newParams };
        handleSearch(mergedParams as T);
      },

      clear: (keys?: keyof T | Array<keyof T>) => {
        if (keys !== undefined) {
          // 清空指定参数（单个或多个）
          const keysArray = Array.isArray(keys) ? keys : [keys];
          const newParams = { ...search };
          keysArray.forEach((key) => {
            delete newParams[key];
          });
          handleSearch(newParams as T);
        } else {
          handleSearch({} as T); // 清空所有参数
        }
      },
    }),
    [search, handleSearch]
  );

  return {
    search,
    searchHandler,
    pending: isPending,
  };
}
